// «Готовность» в кэше пульта (задачи D5 и D7): снимок `GET /api/v1/readiness` (опрос раз в 60 с) и прогон
// `POST /api/v1/readiness/run` (admin). Прогон зовут экран «Готовность» (D7) и команда «Проверить всё» в ⌘K (D5).
//
// Порядок строк на экране меняется только при открытии экрана и по «Проверить всё» (UX §1): прогон всего пишет новый
// снимок в кэш и отмечает «полный прогон» (`fullRunCount`) — по этой отметке экран раскладывает строки заново.
// Прогон отдельных строк («Проверить снова» в панели) раскладку не трогает.
import { ReadinessSnapshotSchema, type ReadinessSnapshot, type RunChecksBody } from '@contract/index.js';
import { queryOptions, type QueryClient } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';
import { apiGet, apiSend } from '../client';
import { qk } from '../keys';

export const READINESS_PATH = '/api/v1/readiness';
export const READINESS_RUN_PATH = '/api/v1/readiness/run';

/** Как часто экран перечитывает снимок, мс: раннер на сервере прогоняет проверки раз в 60 с. */
export const READINESS_POLL_MS = 60_000;

export const readinessQuery = queryOptions({
  queryKey: qk.readiness,
  queryFn: ({ signal }) => apiGet(READINESS_PATH, ReadinessSnapshotSchema, { signal }),
  refetchInterval: READINESS_POLL_MS,
});

// ───────────────────────────── отметка «полный прогон» ─────────────────────────────

let fullRuns = 0;
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function snapshot(): number {
  return fullRuns;
}

/** Сколько раз за жизнь вкладки прошёл «Проверить всё»: экран раскладывает строки заново, когда число растёт. */
export function useFullRunCount(): number {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

function markFullRun(): void {
  fullRuns += 1;
  for (const listener of listeners) listener();
}

/**
 * «Обновить» у «Просмотра» (прогонять проверки ему нельзя): перечитать снимок и разложить строки заново — как
 * «Проверить всё» у админа.
 */
export async function refreshReadiness(queryClient: QueryClient): Promise<void> {
  await queryClient.refetchQueries({ queryKey: qk.readiness, type: 'active' }, { throwOnError: true });
  markFullRun();
}

/**
 * Прогон проверок: без `ids` — всё заново, с `ids` — внешние сервисы только у названных строк. Новый снимок — в кэш,
 * статус Анны (пилюля) перечитывается: вердикт мог смениться.
 */
export async function runChecks(queryClient: QueryClient, ids?: readonly string[]): Promise<ReadinessSnapshot> {
  const body: RunChecksBody = ids === undefined ? {} : { ids: [...ids] };
  const next = await apiSend('POST', READINESS_RUN_PATH, body, ReadinessSnapshotSchema);
  queryClient.setQueryData(qk.readiness, next);
  if (ids === undefined) markFullRun();
  void queryClient.invalidateQueries({ queryKey: qk.status });
  return next;
}
